import { useEffect, useRef, useCallback } from 'react';
import { GameState, GameAction } from '../types/game';
import {
  chooseBotMove,
  botDecideAction,
  chooseBotDiscard,
  getCardsToDiscard,
  chooseTableCardsToDiscard,
  chooseMinLevelCardsFromHand,
} from '../logic/botAI';

type BotState = 'idle' | 'thinking' | 'acting';

interface UseBotOptions {
  enabled: boolean;
  gameState: GameState | null;
  executeAction: (playerId: 1 | 2, action: GameAction) => boolean;
  onCombineAnimation: (cardIds: [string, string]) => void;
  onCombineAnimationEnd: () => void;
}

interface UseBotReturn {
  isActive: boolean;
}

const BOT_PLAYER_ID = 2 as const;
const MIN_THINK_TIME = 1000;
const MAX_THINK_TIME = 2000;
const ANIMATION_DURATION = 1000;

export function useBot({
  enabled,
  gameState,
  executeAction,
  onCombineAnimation,
  onCombineAnimationEnd,
}: UseBotOptions): UseBotReturn {
  const botStateRef = useRef<BotState>('idle');
  const actionsThisTurnRef = useRef(0);
  const lastTurnNumberRef = useRef(-1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if it's bot's turn
  const isBotTurn = enabled &&
    gameState !== null &&
    gameState.winner === null &&
    gameState.currentTurn === BOT_PLAYER_ID;

  // Execute a single bot action
  const executeBotAction = useCallback(() => {
    if (!gameState || botStateRef.current !== 'thinking') return;

    botStateRef.current = 'acting';

    const botHand = gameState.player2.hand;
    const table = gameState.table;
    const decision = botDecideAction(botHand, table);

    if (decision === 'combine') {
      const move = chooseBotMove(botHand, table);

      if (move) {
        const [cardId1, cardId2] = move;

        // Show animation
        onCombineAnimation([cardId1, cardId2]);

        timeoutRef.current = setTimeout(() => {
          const success = executeAction(BOT_PLAYER_ID, { type: 'combine', cardId1, cardId2 });

          if (success) {
            actionsThisTurnRef.current += 1;
          }

          onCombineAnimationEnd();
          botStateRef.current = 'idle';
        }, ANIMATION_DURATION);
      } else {
        // No valid move - end turn
        executeAction(BOT_PLAYER_ID, { type: 'end_turn' });
        botStateRef.current = 'idle';
      }
    } else if (decision === 'discard') {
      let toDiscard: string[] = [];
      let shouldContinueAfterDiscard = false;

      // If hand is full (>7 cards), discard from hand
      if (botHand.length > 7) {
        toDiscard = getCardsToDiscard(botHand, table);

        if (toDiscard.length === 0) {
          const discardCount = Math.min(4, Math.max(1, botHand.length - 7));
          toDiscard = chooseBotDiscard(botHand, table, discardCount);
        }
      } else if (actionsThisTurnRef.current === 0) {
        // No actions yet this turn - discard min level cards from hand
        toDiscard = chooseMinLevelCardsFromHand(botHand);
        shouldContinueAfterDiscard = true;
      } else if (!gameState.player2.discardedThisTurn) {
        // Already made actions, discard from table
        toDiscard = chooseTableCardsToDiscard(table);
      }

      if (toDiscard.length > 0) {
        const success = executeAction(BOT_PLAYER_ID, { type: 'discard', cardIds: toDiscard });

        if (success && shouldContinueAfterDiscard) {
          // After discarding from hand without actions, try again
          botStateRef.current = 'idle';
        } else {
          // End turn after discard
          executeAction(BOT_PLAYER_ID, { type: 'end_turn' });
          botStateRef.current = 'idle';
        }
      } else {
        // Nothing to discard - end turn
        executeAction(BOT_PLAYER_ID, { type: 'end_turn' });
        botStateRef.current = 'idle';
      }
    } else {
      // End turn
      executeAction(BOT_PLAYER_ID, { type: 'end_turn' });
      botStateRef.current = 'idle';
    }
  }, [gameState, executeAction, onCombineAnimation, onCombineAnimationEnd]);

  // Schedule next action with random delay
  const scheduleAction = useCallback(() => {
    if (botStateRef.current !== 'idle') return;

    botStateRef.current = 'thinking';
    const delay = MIN_THINK_TIME + Math.random() * (MAX_THINK_TIME - MIN_THINK_TIME);

    timeoutRef.current = setTimeout(executeBotAction, delay);
  }, [executeBotAction]);

  // Main effect - respond to game state changes
  useEffect(() => {
    // Reset if bot is disabled or game ended
    if (!isBotTurn) {
      botStateRef.current = 'idle';
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Reset counter on new turn
    if (gameState && lastTurnNumberRef.current !== gameState.turnNumber) {
      actionsThisTurnRef.current = 0;
      lastTurnNumberRef.current = gameState.turnNumber;
    }

    // Start thinking if idle
    if (botStateRef.current === 'idle') {
      scheduleAction();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isBotTurn, gameState, scheduleAction]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      botStateRef.current = 'idle';
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return {
    isActive: botStateRef.current !== 'idle',
  };
}
